import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { useInput } from 'react-admin';
import { TextField, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
    textFieldRoot: {
        margin: '8px 0'
    },
    inputRoot: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        padding: '11px 8px',
        minHeight: 36,
        cursor: 'text',
        width: '100%',
    },
    chip: {
        margin: '2px 4px 2px 0',
    },
    realInput: {
        border: 'none',
        outline: 'none',
        flex: 1,
        minWidth: 60,
        font: 'inherit',
        padding: 4,
        width: '100%'
    },
}));

const TagsInputComponent = forwardRef((props, ref) => {
    const { tags, current, setCurrent, addTag, removeTag, classes, onFocus, onBlur } = props;
    const inputRef = useRef();

    const handleBlur = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            onBlur?.();
        }
    };

    return (
        <div
            ref={ref}
            className={classes.inputRoot}
            onClick={() => {
                inputRef.current.focus();
                onFocus?.();
            }}
            onBlur={handleBlur}
            tabIndex={-1}
        >
            {tags.map(tag => (
                <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => removeTag(tag)}
                    className={classes.chip}
                />
            ))}
            <input
                ref={inputRef}
                value={current}
                onChange={e => setCurrent(e.target.value)}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addTag(current);
                        setCurrent('');
                        return;
                    }
                    if (e.key === 'Backspace' && current === '' && tags.length) {
                        e.preventDefault();
                        removeTag(tags[tags.length - 1]);
                    }
                }}
                className={classes.realInput}
                onFocus={onFocus}
                onBlur={handleBlur}
            />
        </div>
    );
});

const TagsTextInput = ({ source, label, variant = 'outlined', ...props }) => {
    const classes = useStyles();

    const {
        input,
        meta: { touched, error },
    } = useInput({ source, ...props });

    const { value: raValue, onChange: raOnChange } = input;

    const [tags, setTags] = useState(Array.isArray(raValue) ? raValue : []);
    const [current, setCurrent] = useState('');
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (Array.isArray(raValue)) setTags(raValue);
    }, [raValue]);

    const addTag = (val) => {
        const text = val.trim();
        let splitValue = null;
        if (text.includes(',')) splitValue = ',';
        else if (text.includes(';')) splitValue = ';';
        else if (text.includes(' ')) splitValue = ' ';
        else if (text.includes('\n')) splitValue = '\n';
        else if (text.includes('\t')) splitValue = '\t';
        
        if (splitValue) {
            const tagsSplit = text.split(splitValue).map(t => t.trim()).filter(t => t);
            const newTags = Array.from(new Set([...tags, ...tagsSplit]));
            setTags(newTags);
            raOnChange(newTags);
            return;
        }
        const t = text;
        if (t && !tags.includes(t)) {
            const newTags = [...tags, t];
            setTags(newTags);
            raOnChange(newTags);
            input.onBlur();
        }
    };

    const removeTag = (tag) => {
        const newTags = tags.filter(t => t !== tag);
        setTags(newTags);
        raOnChange(newTags);
        input.onBlur();
    };

    return (
        <TextField
            label={label}
            variant={variant}
            fullWidth
            className={classes.textFieldRoot}
            error={touched && !!error}
            helperText={touched && error}
            InputLabelProps={{
                shrink: focused || tags.length > 0 || current.length > 0,
            }}
            InputProps={{
                inputComponent: TagsInputComponent,
                inputProps: {
                    tags,
                    current,
                    setCurrent,
                    addTag,
                    removeTag,
                    classes,
                    onFocus: (e) => {
                        setFocused(true);
                        input.onFocus(e);
                    },
                    onBlur: (e) => {
                        setFocused(false);
                        input.onBlur(e);
                    },
                },
            }}
            {...props}
        />
    );
};

export default TagsTextInput;